import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import { ScaffolderInputHeader } from '../../../components';
import { DEPLOYMENT_TYPE_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import {
  DEFAULT_HELM_CONFIGURATION,
  DEFAULT_SOURCE_CONFIGURATION,
  HelmConfiguration,
  SourceConfiguration,
} from '../types';
import { inferRepoType, resolveRepoType } from '../utils';
import { HelmValuesConfig } from './HelmValuesConfig';
import { SourceFields } from './SourceFields';

type DeploymentType = NonNullable<SourceConfiguration['deploymentType']>;

const DEPLOYMENT_TYPE_LABELS: Record<DeploymentType, string> = {
  manifests: 'Manifests',
  helm: 'Helm',
  kustomize: 'Kustomize',
};

const DEPLOYMENT_TYPE_OPTIONS: DeploymentType[] = [
  'manifests',
  'helm',
  'kustomize',
];

function normalizeSourceRepoFields(
  source: SourceConfiguration,
): SourceConfiguration {
  const next: SourceConfiguration = { ...source };
  const inferred = inferRepoType(next.repoURL);

  if (inferred !== null || next.deploymentType !== 'helm') {
    delete next.repoType;
  }

  const repoType = resolveRepoType(next);
  if (repoType === 'git') {
    delete next.chart;
  } else if (repoType === 'helm') {
    delete next.path;
  } else {
    delete next.path;
    delete next.chart;
  }

  return next;
}

export interface SourcePickerProps {
  value: SourceConfiguration | null;
  onChange: (value: SourceConfiguration | null) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

export const SourcePicker = (props: SourcePickerProps) => {
  const { value, onChange, errors, enableAppSetSupport = false } = props;
  const { classes } = useStyles();
  const data = value ?? DEFAULT_SOURCE_CONFIGURATION;

  const handleSourceChange = (next: SourceConfiguration | null) => {
    onChange(next ? normalizeSourceRepoFields(next) : next);
  };

  const handleDeploymentTypeChange = (
    next: SourceConfiguration['deploymentType'],
  ) => {
    const base: SourceConfiguration = {
      ...data,
      deploymentType: next,
    };
    if (next === 'helm') {
      base.helm = data.helm ?? { ...DEFAULT_HELM_CONFIGURATION };
    } else {
      delete base.helm;
      delete base.chart;
    }
    handleSourceChange(base);
  };

  const handleHelmChange = (helm: HelmConfiguration) => {
    handleSourceChange({ ...data, helm });
  };

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Deployment Type"
          description="How ArgoCD interprets the application source."
          infoDoc={DEPLOYMENT_TYPE_INFO}
        />
      </Box>
      <Box className={classes.inputContainer}>
        <Autocomplete
          size="small"
          options={DEPLOYMENT_TYPE_OPTIONS}
          value={data.deploymentType}
          getOptionLabel={option => DEPLOYMENT_TYPE_LABELS[option]}
          onChange={(_, option) => handleDeploymentTypeChange(option)}
          renderInput={params => (
            <TextField
              {...params}
              label="Deployment Type"
              variant="outlined"
              margin="none"
              fullWidth
              required
              error={Boolean(errors?.length > 0) && !data.deploymentType}
              helperText={
                data.deploymentType
                  ? undefined
                  : 'Select a deployment type to continue.'
              }
            />
          )}
        />
      </Box>

      {data.deploymentType && (
        <>
          <SourceFields
            source={data}
            onChange={handleSourceChange}
            enableAppSetSupport={enableAppSetSupport}
            errors={errors}
          />
          {data.deploymentType === 'helm' && (
            <HelmValuesConfig
              helm={data.helm ?? DEFAULT_HELM_CONFIGURATION}
              onChange={handleHelmChange}
              enableAppSetSupport={enableAppSetSupport}
              errors={errors}
            />
          )}
        </>
      )}
    </Box>
  );
};
